import { ActionReducerMapBuilder, createAsyncThunk } from "@reduxjs/toolkit";
import { graphqlRequest } from "context/graphql/functions";
import { initializePartnerVisit } from "helpers/initializeJobVisit";
import { isEqual } from "lodash";
import { AppAsyncThunkConfig } from "store";
import { State } from "./partner.store";

export const createAppAsyncThunk = createAsyncThunk.withTypes<AppAsyncThunkConfig>();

export const asyncQueries = {
  getPartnerJob: createAppAsyncThunk(
    "partner/getPartnerJob",
    async (jobId: string, { getState, rejectWithValue, extra: { sdk } }) => {
      const { data, errors } = await graphqlRequest(sdk.getJob, {
        variables: {
          id: jobId,
        },
      });
      if (errors) return rejectWithValue(errors);
      if (!data?.job) return rejectWithValue("something went wrong");
      return {
        data,
        selectedJobId: data.job.id,
      };
    },
  ),

  refreshJob: createAppAsyncThunk(
    "partner/refreshJob",
    async (_, { getState, rejectWithValue, extra: { sdk } }) => {
      const {
        partner: { selectedJobId, token },
      } = getState();
      if (!selectedJobId || !token) return rejectWithValue("No selected job");
      const { data, errors } = await graphqlRequest(sdk.refreshJob, {
        variables: {
          jobId: selectedJobId,
        },
      });
      if (errors) return rejectWithValue(errors);
      if (!data?.job || !data?.files || !data?.workNotes)
        return rejectWithValue("something went wrong");
      return {
        data,
        selectedJobId,
      };
    },
  ),

  getFiles: createAppAsyncThunk(
    "partner/getFiles",
    async (_, { getState, rejectWithValue, extra: { sdk } }) => {
      const {
        partner: { token, selectedJobId },
      } = getState();
      if (!token || !selectedJobId) return rejectWithValue("No selected job");
      const { data, errors } = await graphqlRequest(sdk.getFiles, {
        variables: {
          jobId: selectedJobId,
        },
      });
      if (errors) return rejectWithValue(errors);
      if (!data?.files) return rejectWithValue("something went wrong");
      return { data, selectedJobId };
    },
  ),
  getNotes: createAppAsyncThunk(
    "partner/getNotes",
    async (_, { getState, rejectWithValue, extra: { sdk } }) => {
      const {
        partner: { token, selectedJobId },
      } = getState();
      if (!token || !selectedJobId) return rejectWithValue("No selected job");
      const { data, errors } = await graphqlRequest(sdk.getWorkNotes, {
        variables: {
          jobId: selectedJobId,
        },
      });
      if (errors) return rejectWithValue(errors);
      if (!data?.workNotes) return rejectWithValue("something went wrong");
      return { data, selectedJobId };
    },
  ),
  getComments: createAppAsyncThunk(
    "partner/getComments",
    async (_, { getState, rejectWithValue, extra: { sdk } }) => {
      const {
        partner: { token, selectedJobId },
      } = getState();
      if (!token || !selectedJobId) return rejectWithValue("No selected job");
      const { data, errors } = await graphqlRequest(sdk.getComments, {
        variables: {
          jobId: selectedJobId,
        },
      });
      if (errors) return rejectWithValue(errors);
      if (!data?.comments) return rejectWithValue("something went wrong");
      return { data, selectedJobId };
    },
  ),

  //Contacts
  getContacts: createAppAsyncThunk(
    "partner/getContacts",
    async (_, { rejectWithValue, getState, extra: { sdk } }) => {
      const {
        partner: { jobs, selectedJobId, token },
      } = getState();
      if (!token || !selectedJobId) return rejectWithValue("No selected job");
      const { customer } = jobs[selectedJobId];
      if (!customer || !customer.id) return rejectWithValue("No customer");
      const { data, errors } = await graphqlRequest(sdk.getContacts, {
        variables: {
          customerId: customer?.id,
        },
      });
      if (errors) return rejectWithValue(errors);
      if (!data?.contacts) return rejectWithValue("something went wrong");
      return data.contacts;
    },
  ),
};

export const queryBuilder = (builder: ActionReducerMapBuilder<State>) => {
  builder.addCase(asyncQueries.getPartnerJob.pending, (state) => {
    state.loadingJob = true;
    return state;
  });
  builder.addCase(asyncQueries.getPartnerJob.rejected, (state) => {
    state.loadingJob = false;
    return state;
  });
  builder.addCase(
    asyncQueries.getPartnerJob.fulfilled,
    (state, { payload: { data, selectedJobId } }) => {
      let { job } = data;
      if (!selectedJobId || !job) return state;
      state.selectedJobId = selectedJobId;

      if (!state.jobs[selectedJobId]) {
        state.jobs[selectedJobId] = {
          ...job,
          files: [],
          workNotes: [],
          visits: [],
          comments: [],
        };
        initializePartnerVisit(state, state.jobs[selectedJobId]);
      } else {
        state.jobs[selectedJobId] = {
          ...state.jobs[selectedJobId],
          ...job,
          files: state.jobs[selectedJobId].files || [],
          workNotes: state.jobs[selectedJobId].workNotes || [],
          visits: state.jobs[selectedJobId].visits || [],
          comments: [],
        };
        if (!state.jobVisits[selectedJobId]) {
          initializePartnerVisit(state, state.jobs[selectedJobId]);
        }
      }

      state.loadingJob = false;
      return state;
    },
  );
  builder.addCase(asyncQueries.refreshJob.pending, (state) => {
    state.loadingJob = true;
    return state;
  });
  builder.addCase(asyncQueries.refreshJob.rejected, (state) => {
    state.loadingJob = false;
    return state;
  });
  builder.addCase(
    asyncQueries.refreshJob.fulfilled,
    (state, { payload: { data, selectedJobId } }) => {
      let { job } = data;
      if (!selectedJobId || !job || !state.jobs[selectedJobId]) return state;
      const oldFiles = state.jobs[selectedJobId].files;
      const oldPreorderedParts = state.jobs[selectedJobId].preOrderedParts;
      const oldSymptomText = state.jobs[selectedJobId].symptomDescription;
      const oldInternalNotes = state.jobs[selectedJobId].workNotes;
      state.jobs[selectedJobId] = {
        ...state.jobs[selectedJobId],
        ...job,
        files: [...data.files],
        workNotes: [...data.workNotes],
        visits: state.jobs[selectedJobId].visits || [],
      };
      state.refreshJobNotifications.symptomText = !isEqual(
        oldSymptomText,
        data.job?.symptomDescription,
      );
      state.refreshJobNotifications.internalNotes =
        data.workNotes.length !== oldInternalNotes.length ||
        !isEqual(oldInternalNotes, data.workNotes);
      state.refreshJobNotifications.files =
        data.files.length !== oldFiles.length || !isEqual(oldFiles, data.files);
      state.refreshJobNotifications.preOrderedParts =
        data.job?.preOrderedParts.length !== oldPreorderedParts.length ||
        !isEqual(oldPreorderedParts, data.job.preOrderedParts);

      if (!state.jobVisits[job.id]) {
        initializePartnerVisit(state, state.jobs[selectedJobId]);
      }
      state.loadingJob = false;
      return state;
    },
  );

  builder.addCase(
    asyncQueries.getFiles.fulfilled,
    (state, { payload: { data, selectedJobId } }) => {
      if (!selectedJobId) return state;
      state.jobs[selectedJobId].files = [...data.files];
      return state;
    },
  );
  builder.addCase(
    asyncQueries.getNotes.fulfilled,
    (state, { payload: { data, selectedJobId } }) => {
      if (!selectedJobId) return state;
      state.jobs[selectedJobId].workNotes = [...data.workNotes];
      return state;
    },
  );
  // Comments
  builder.addCase(asyncQueries.getComments.pending, (state) => {
    state.commentsLoading = true;
    return state;
  });
  builder.addCase(asyncQueries.getComments.rejected, (state) => {
    state.commentsLoading = false;
    return state;
  });
  builder.addCase(
    asyncQueries.getComments.fulfilled,
    (state, { payload: { data, selectedJobId } }) => {
      if (!selectedJobId) return state;
      state.commentsLoading = false;
      state.jobs[selectedJobId].comments = [...data.comments];
      return state;
    },
  );
  // Contacts
  builder.addCase(asyncQueries.getContacts.rejected, (state, { payload: errors }) => {
    state.contactsLoading = false;
    return state;
  });
  builder.addCase(asyncQueries.getContacts.pending, (state) => {
    state.contactsLoading = true;
    return state;
  });
  builder.addCase(asyncQueries.getContacts.fulfilled, (state) => {
    state.contactsLoading = false;
    return state;
  });
};
